<template>
  <v-snackbar
    v-model="showPrompt"
    location="top center"
    timeout="-1"
    class="update-snackbar"
    content-class="update-snackbar-content"
  >
    <div class="d-flex align-center ga-3">
      <v-icon color="#7c4dff" size="22">mdi-arrow-up-circle</v-icon>
      <span class="update-text">{{ $t('New update available') }}</span>
    </div>

    <template #actions>
      <v-btn
        variant="flat"
        size="small"
        rounded="lg"
        class="update-btn-apply"
        @click="skipWaiting"
      >
        {{ $t('Update Now') }}
      </v-btn>
      <v-btn
        icon
        variant="text"
        size="x-small"
        class="update-btn-close"
        @click="dismissUpdate"
      >
        <v-icon size="18">mdi-close</v-icon>
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'

const registration = ref<ServiceWorkerRegistration | null>(null)
const waitingWorker = ref<ServiceWorker | null>(null)
const installingWorker = ref<ServiceWorker | null>(null)
const activeWorker = ref<ServiceWorker | null>(null)
const showPrompt = ref(false)
const updating = ref(false)

const skipWaiting = () => {
  if (!('serviceWorker' in navigator)) {
    window.location.reload()
    return
  }

  if (waitingWorker.value) {
    // send the message to the waiting worker and show updating state
    waitingWorker.value.postMessage({ type: 'SKIP_WAITING' })
    showPrompt.value = false
    updating.value = true

    // Wait for the new service worker to take control (controllerchange)
    let handled = false
    const onControllerChange = () => {
      if (handled) return
      handled = true
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange
      )
      window.location.reload()
    }

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange
    )

    // Fallback: if controllerchange doesn't fire, reload after 5s
    setTimeout(() => {
      if (!handled) {
        handled = true
        navigator.serviceWorker.removeEventListener(
          'controllerchange',
          onControllerChange
        )
        window.location.reload()
      }
    }, 5000)
    return
  }

  // no waiting worker, just reload
  window.location.reload()
}

const dismissUpdate = () => {
  showPrompt.value = false
}

// Watch for changes in the waiting worker
watch(
  () => waitingWorker.value,
  () => {
    if (waitingWorker.value) {
      showPrompt.value = true
    }
  },
  { immediate: true }
)

onMounted(async () => {
  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) return

    registration.value = reg
    activeWorker.value = reg.active || null
    waitingWorker.value = reg.waiting || null
    installingWorker.value = reg.installing || null

    // Watch for changes from server
    reg.addEventListener('updatefound', () => {
      const intervalId = setInterval(() => {
        if (reg.waiting) {
          waitingWorker.value = reg.waiting
          clearInterval(intervalId)
        }
      }, 1000)
      registration.value = reg
      installingWorker.value = reg.installing
    })

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      activeWorker.value = navigator.serviceWorker.controller
      waitingWorker.value = null
      installingWorker.value = null
      showPrompt.value = false
    })
  }
})

onUnmounted(() => {
  registration.value = null
  waitingWorker.value = null
  installingWorker.value = null
  activeWorker.value = null
})
</script>

<style scoped lang="scss">
.update-snackbar {
  :deep(.v-snackbar__wrapper) {
    border-radius: 14px;
    border: 2px solid transparent;
    background:
      linear-gradient(#fff 0 0) padding-box,
      var(--linearHotPinkDodgerBlue) border-box;
    box-shadow:
      0 4px 16px rgba(74, 114, 255, 0.12),
      0 2px 6px rgba(0, 0, 0, 0.05);
    min-width: auto;
    overflow: visible;
  }

  :deep(.v-snackbar__content) {
    padding: 12px 8px 12px 20px;
  }

  :deep(.v-snackbar__actions) {
    margin-inline-end: 8px;
    gap: 4px;
  }
}

.update-text {
  font-family: Fredoka, sans-serif;
  font-weight: 500;
  font-size: 0.9rem;
  color: #1f2d37;
}

.update-btn-apply {
  background: var(--linearHotPinkDodgerBlue) !important;
  color: white !important;
  font-family: Fredoka, sans-serif;
  font-weight: 500;
  font-size: 0.82rem;
  text-transform: none;
  letter-spacing: 0;
}

.update-btn-close {
  color: #8a9199 !important;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}
</style>
